import React, { useEffect } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { useLocation } from 'react-router-dom';
import DocNavigation from '../components/DocNavigation';
import { CopyBlock } from 'react-code-blocks';
import { dracula } from 'react-code-blocks';

const SweekarConfig: React.FC = React.memo(() => {
	const location = useLocation();

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	}, [location.pathname]);

	return (
		<Box sx={{ paddingLeft: 4 }}>
			<Typography
				sx={{
					fontWeight: 'bold',
					fontSize: { xs: '1.2rem', sm: '1.5rem' },
					color: '#0b3452',
					display: 'inline-block',
					marginBottom: 0,
					lineHeight: 1.5,
				}}
			>
				Finvu AA: Tailor Every Screen to
                Match Your Brand
			</Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
        <img
          src="/images/consentScreen.png"
          alt="Profile Discovery Image"
          style={{
            width: "100%",
            maxWidth: "800px",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />
        </Box>

			{/* <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				Legal Framework
			</Typography> */}

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
            With Finvu's highly customizable Account Aggregator (AA) platform, partners
            have full control over how the AA consent journey looks and feels. From visual
            design to user flow configurations, every element is configurable—allowing for
            consistent branding, intuitive experiences, and seamless integration with your
            existing digital touchpoints.
			</Typography>

            <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				1. Smart FIP Discovery Options with Flexible Account Selection
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
            Finvu allows you to define how the journey flows, what steps appear to users, and
            in what order. Some examples of these experience-level customizations include:
			</Typography>

            <ul>
                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        <Box component="span" fontWeight="bold">Enable Auto Discovery:</Box> Enable FIUs to auto discover accounts (up to 5 FIPs)
                        simultaneously.
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                    <Box component="span" fontWeight="bold">Multi-FIP selection option:</Box> Allow users to select multiple FIPs (up to five) at
                    once, reducing effort and time.
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                    <Box component="span" fontWeight="bold">Control Journey Completion:</Box> Choose whether the journey ends with a "Close
                    Window" message or a redirection to your own app or site.
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                    <Box component="span" fontWeight="bold">Flexible Account Selection:</Box> Pre-select some user accounts for faster
                    journeys, or allow users to manually select accounts for more control.
                    </Typography>
                </li>
                
            </ul>

            <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                <Box component="span" fontWeight="bold"> Why it matters:</Box> These configurations help reduce user drop-offs and improve
                engagement by minimizing friction.
            </Typography>

            <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				2. Control over display and selection of accounts to be shared
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
            You can choose to display only certain types of accounts—like savings or current
            accounts—and even limit how many accounts a user can select.
			</Typography>

            <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7,fontWeight: 'bold' }}>
                Key Benefits:
            </Typography>
            <ul>
                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Simplifies choices for users
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Highlights relevant accounts based on use case.
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Aligns with compliance or product-specific needs.
                    </Typography>
                </li>                
            </ul>

            <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				3. FIP Management & Discovery Controls
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
            Control which financial institutions (FIPs) appear to your users during their
            journey:
			</Typography>

            <ul>
                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Hide specific FIPs from being shown during account discovery
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Exclude FIPs completely if data is being fetched from your own sources
                    </Typography>
                </li>
              
            </ul>

            <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                <Box component="span" fontWeight="bold"> Why it matters:</Box> : Helps avoid redundancy, reduces confusion, and improves clarity
                during the account linking process.
            </Typography>

            <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				4. Improved Error Visibility & Customizable Messages
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                Enhance user experience by configuring inline error messages or toast message.
                Additionally, you can customize key system texts, such as:
			</Typography>

            <ul>
                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Call-to-action (CTA) button names like “Accept Consent” / “Deny Consent”
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Informational text and instructions across screens
                    </Typography>
                </li>
              
            </ul>

            <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                <Box component="span" fontWeight="bold"> Why it matters:</Box> :Inline and contextual messages reduce confusion and guide users more
                effectively, resulting in better clarity, trust, and higher conversion rates.
            </Typography>

            <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				5. Streamlined Consent Management
			</Typography>

			<Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                Design how multiple consents appear to users:
			</Typography>

            <ul>
                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Rank consents in a specific order
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Choose which are mandatory or optional
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        Pre-select optional consents for faster approval
                    </Typography>
                </li>
              
            </ul>

            <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                <Box component="span" fontWeight="bold"> Why it matters:</Box> Ensures full control and transparency while keeping journeys
                compliant and user-friendly.
            </Typography>

			<Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				6. Visual Branding & Theming
			</Typography>

            <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                Control the look and feel of the entire AA journey, including:
            </Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
        <img
          src="/images/loginScreen.png"
          alt="Profile Discovery Image"
          style={{
            width: "100%",
            maxWidth: "800px",
            height: "auto",
            display: "block",
            margin: "0 auto",
          }}
        />

           <ul>
                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        <Box component="span" fontWeight="bold">Colors</Box> (primary, secondary, buttons, etc.)
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        <Box component="span" fontWeight="bold">Fonts</Box> and <Box component="span" fontWeight="bold">font styles</Box>
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        <Box component="span" fontWeight="bold">Logos</Box> (main and sub-branding)
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        <Box component="span" fontWeight="bold">Themes</Box> (light/dark modes)
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 ,fontWeight: 'bold'}}>
                        Title Text
                    </Typography>
                </li>

                <li>
                    <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
                        <Box component="span" fontWeight="bold">UI element shapes</Box> (rounded corners, button sizes, etc.)
                    </Typography>
                </li>
              
            </ul>
            

        </Box>

        <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				7. Multi-Profile Support
		</Typography>

        <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
            If you operate with multiple partners or use cases, Finvu allows for profile-based
            customization—where each profile under the same fiu_id can have its unique
            branding, optimized journey without requiring multiple implementations.
        </Typography>
        
        <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
            <Box component="span" fontWeight="bold"> Why it matters:</Box> Flexibility at scale. Tailor experiences per profile wise while
            keeping your tech stack lean.
        </Typography>

        <Typography
				variant="subtitle1"
				sx={{ mt: 4, color: '#0b3452', fontWeight: 'bold' }}
			>
				Ready to Customize?
		</Typography>

        <Typography sx={{ mt: 2, color: '#555', lineHeight: 1.7 }}>
        Finvu's configurable AA platform is built to adapt to your needs. Let us help you
        deliver consistent, branded, and efficient user journeys.
        </Typography>
        
		    <DocNavigation currentPath={location.pathname} />
		</Box>
	);
});

export default SweekarConfig;
